/**
 * ATL-1694 — the app's side of connecting Claude / ChatGPT to Atlas over MCP.
 *
 *  - `info()` feeds Settings → AI assistants: the MCP server address to paste into the assistant,
 *    whether this organization may connect one, and whether the signed-in person may approve it.
 *  - `getRequest` / `approve` / `deny` drive the consent screen the backend's OAuth authorize
 *    endpoint sends the browser to (`/connect-ai?request=<id>`). Admins only — the backend refuses
 *    anyone else with 403, and re-checks every rule on approve; the screen only shows what it says.
 *
 * Responses are checked for the fields the screens depend on and refused loudly when one is
 * missing, rather than rendered as "not available" or as a consent page with no scopes.
 */
import type { AxiosInstance } from 'axios'
import apiClient from '../API/apiClient'

export type AiAvailabilityReason =
  | 'mcp_disabled'
  | 'mcp_not_enabled_for_organization'
  | 'mcp_access_undetermined'
  | 'organization_not_on_supabase'
  | 'organization_access_undetermined'

export interface AiAvailability {
  available: boolean
  reason: AiAvailabilityReason | null
}

export interface AiScope {
  scope: string
  kind: 'read' | 'draft'
}

export interface AiAssistantsInfo {
  serverUrl: string
  isAdmin: boolean
  availability: AiAvailability
  scopes: AiScope[]
}

export type AiClientKind = 'claude' | 'chatgpt' | 'local' | 'other'

export interface AiClient {
  /** Self-asserted by the client at registration — never the thing a person should trust. */
  name: string | null
  kind: AiClientKind
  /** The host the authorization code is sent to — the identity the person is actually trusting. */
  host: string
}

export type ConnectRequestStatus = 'pending' | 'approved' | 'denied' | 'exchanged' | 'expired'

export interface ConnectRequest {
  id: string
  status: ConnectRequestStatus
  client: AiClient
  requestedScopes: string[]
  defaultExpiresInDays: number
  allowedExpiresInDays: number[]
  organization: { name: string }
  availability: AiAvailability
  expiresAt: string
}

const CLIENT_KINDS: readonly AiClientKind[] = ['claude', 'chatgpt', 'local', 'other']
const REQUEST_STATUSES: readonly ConnectRequestStatus[] = ['pending', 'approved', 'denied', 'exchanged', 'expired']

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object'

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'string')

const isAvailability = (value: unknown): value is AiAvailability =>
  isRecord(value) && typeof value.available === 'boolean' && (value.reason === null || typeof value.reason === 'string')

export const isAiClient = (value: unknown): value is AiClient =>
  isRecord(value) &&
  (value.name === null || typeof value.name === 'string') &&
  typeof value.kind === 'string' &&
  (CLIENT_KINDS as readonly string[]).includes(value.kind) &&
  typeof value.host === 'string' &&
  value.host.length > 0

const parseInfo = (data: unknown): AiAssistantsInfo => {
  if (
    !isRecord(data) ||
    typeof data.serverUrl !== 'string' ||
    !/^https?:\/\//.test(data.serverUrl) ||
    typeof data.isAdmin !== 'boolean' ||
    !isAvailability(data.availability) ||
    !Array.isArray(data.scopes)
  ) {
    throw new Error('ai assistants info response is malformed')
  }
  const scopes = data.scopes.map((s): AiScope => {
    if (!isRecord(s) || typeof s.scope !== 'string' || (s.kind !== 'read' && s.kind !== 'draft')) {
      throw new Error('ai assistants info response is malformed')
    }
    return { scope: s.scope, kind: s.kind }
  })
  return { serverUrl: data.serverUrl, isAdmin: data.isAdmin, availability: data.availability, scopes }
}

const parseRequest = (data: unknown): ConnectRequest => {
  if (
    !isRecord(data) ||
    typeof data.id !== 'string' ||
    typeof data.status !== 'string' ||
    !(REQUEST_STATUSES as readonly string[]).includes(data.status) ||
    !isAiClient(data.client) ||
    !isStringArray(data.requestedScopes) ||
    typeof data.defaultExpiresInDays !== 'number' ||
    !Array.isArray(data.allowedExpiresInDays) ||
    !data.allowedExpiresInDays.every((d) => typeof d === 'number') ||
    !isRecord(data.organization) ||
    typeof data.organization.name !== 'string' ||
    !isAvailability(data.availability) ||
    typeof data.expiresAt !== 'string'
  ) {
    throw new Error('connect request response is malformed')
  }
  return {
    id: data.id,
    status: data.status as ConnectRequestStatus,
    client: data.client,
    requestedScopes: data.requestedScopes,
    defaultExpiresInDays: data.defaultExpiresInDays,
    allowedExpiresInDays: data.allowedExpiresInDays as number[],
    organization: { name: data.organization.name },
    availability: data.availability,
    expiresAt: data.expiresAt,
  }
}

const parseRedirect = (data: unknown): string => {
  const redirectTo = isRecord(data) ? data.redirectTo : undefined
  // The browser is about to be sent there: anything but an absolute http(s) URL is refused.
  if (typeof redirectTo !== 'string' || !/^https?:\/\//i.test(redirectTo)) {
    throw new Error('connect decision response is malformed')
  }
  return redirectTo
}

export const createAiAssistantsApi = (http: AxiosInstance) => ({
  async info(): Promise<AiAssistantsInfo> {
    const { data } = await http.get('/ai-assistants/info')
    return parseInfo(data)
  },

  async getRequest(requestId: string): Promise<ConnectRequest> {
    const { data } = await http.get(`/oauth/requests/${encodeURIComponent(requestId)}`)
    return parseRequest(data)
  },

  /** Returns the URL to send the browser to — the assistant's callback, carrying the code. */
  async approve(requestId: string, input: { scopes: string[]; expiresInDays: number }): Promise<string> {
    const { data } = await http.post(`/oauth/requests/${encodeURIComponent(requestId)}/approve`, input)
    return parseRedirect(data)
  },

  /** Returns the URL to send the browser to — the assistant's callback, carrying `access_denied`. */
  async deny(requestId: string): Promise<string> {
    const { data } = await http.post(`/oauth/requests/${encodeURIComponent(requestId)}/deny`, {})
    return parseRedirect(data)
  },
})

/** The HTTP status of a failed request, or `null` when it never got a response (network). */
export const httpStatusOf = (error: unknown): number | null => {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status
  return typeof status === 'number' ? status : null
}

/** Production instance bound to the shared authenticated axios client. */
export const aiAssistantsApi = createAiAssistantsApi(apiClient)
export type AiAssistantsApi = ReturnType<typeof createAiAssistantsApi>
